import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import zhCN from './locales/zh-CN.ts';
import zhTW from './locales/zh-TW.ts';
import enUS from './locales/en-US.ts';

export const LANGUAGE_STORAGE_KEY = 'app-language';

export const SUPPORTED_LANGUAGES = ['zh-CN', 'zh-TW', 'en-US'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const normalizeLanguage = (value: unknown): SupportedLanguage => {
  if (typeof value !== 'string') return 'zh-CN';
  if (value === 'zh' || value === 'zh-CN' || value === 'zh-Hans' || value === 'zh-Hans-CN') return 'zh-CN';
  if (value === 'zh-TW' || value === 'zh-Hant' || value === 'zh-Hant-TW') return 'zh-TW';
  if (value === 'en' || value === 'en-US') return 'en-US';
  return 'zh-CN';
};

export const getInitialLanguage = (): SupportedLanguage => {
  try {
    return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch (error) {
    console.error('Failed to get language from storage:', error);
    return 'zh-CN';
  }
};

try {
  i18n
    .use(initReactI18next)
    .init({
      resources: {
        'zh-CN': { translation: zhCN },
        'zh-TW': { translation: zhTW },
        'en-US': { translation: enUS },
      },
      lng: getInitialLanguage(),
      fallbackLng: 'zh-CN',
      interpolation: { escapeValue: false },
    });
} catch (error) {
  console.error('i18n init failed, retrying with defaults:', error);
  // Retry with minimal config to ensure translations work
  try {
    i18n.init({
      resources: { 'zh-CN': { translation: zhCN } },
      lng: 'zh-CN',
      fallbackLng: 'zh-CN',
      interpolation: { escapeValue: false },
    });
  } catch (e) {
    console.error('i18n init completely failed:', e);
  }
}

i18n.on('languageChanged', (lng) => {
  const normalized = normalizeLanguage(lng);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  } catch (error) {
    console.error('Failed to save language to storage:', error);
  }
  document.documentElement.lang = normalized;
  document.title = i18n.t('app.title');
});

document.documentElement.lang = getInitialLanguage();
document.title = i18n.t('app.title');

export default i18n;
